import type { FC, ReactNode } from 'react'

import { Clock, Mail as MailIcon, MapPin as MapPinIcon } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { SiteHeader } from '@/components/landing/site-header'
import { SiteFooter } from '@/components/landing/site-footer'
import { ContactForm } from '@/components/landing/contact-form'
import { Reveal } from '@/components/landing/reveal'
import { SectionEyebrow } from '@/components/landing/section-eyebrow'
import { RouteAccent } from '@/components/landing/route-accent'

const details = [
    {
        icon: MailIcon,
        label: 'Email us',
        value: 'hello@marathon.run',
        accent: '#E94E1B'
    },
    {
        icon: MapPinIcon,
        label: 'Find us',
        value: 'Portland, Oregon',
        accent: '#168F4E'
    },
    {
        icon: Clock,
        label: 'Response time',
        value: 'Within 1–2 business days',
        accent: '#C42E5A'
    }
]

const Contact: FC = (): ReactNode => {
    return (
        <div id='top' className='min-h-screen bg-[#0b0b0d]'>
            <Seo
                title='Contact'
                description='Get in touch with the Marathon team. Questions, feedback, or partnership ideas welcome.'
            />
            <SiteHeader />

            <main>
                <section className='relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-20'>
                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute left-1/2 top-0 h-[380px] w-[780px] -translate-x-1/2 rounded-full opacity-30 blur-[120px]'
                        style={{
                            background:
                                'radial-gradient(circle, rgba(233,78,27,0.5) 0%, rgba(233,78,27,0) 70%)'
                        }}
                    />
                    <Reveal className='relative mx-auto flex max-w-3xl flex-col items-center px-5 text-center sm:px-8'>
                        <SectionEyebrow align='center'>GET IN TOUCH</SectionEyebrow>
                        <h1 className='mt-5 font-display text-5xl font-medium leading-[1.03] tracking-tight text-white sm:text-6xl'>
                            Let’s talk running
                        </h1>
                        <RouteAccent className='mt-7 h-6 w-52' />
                        <p className='mx-auto mt-7 max-w-xl text-lg leading-relaxed text-white/60'>
                            Whether it’s a question, a bug, or a partnership idea, drop us
                            a line. Every message lands with a real person on the team.
                        </p>
                    </Reveal>
                </section>

                <section className='pb-24 sm:pb-32'>
                    <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                        <div className='grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16'>
                            <Reveal className='flex flex-col gap-5'>
                                {details.map((item) => {
                                    const Icon = item.icon
                                    return (
                                        <div
                                            key={item.label}
                                            className='group flex items-start gap-4 rounded-2xl border border-white/10 bg-[#101013] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-white/20'
                                        >
                                            <span
                                                className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-white/10 transition-transform duration-300 group-hover:scale-110'
                                                style={{
                                                    backgroundColor: `${item.accent}1a`,
                                                    color: item.accent
                                                }}
                                            >
                                                <Icon className='h-5 w-5' />
                                            </span>
                                            <div>
                                                <div className='text-xs font-bold tracking-[0.16em] text-white/50'>
                                                    {item.label.toUpperCase()}
                                                </div>
                                                <div className='mt-1 text-base font-medium text-white'>
                                                    {item.value}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}

                                <div className='relative mt-1 hidden overflow-hidden rounded-2xl border border-white/10 lg:block'>
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src='https://pub-dc2abe5caae44240a6d1913566a75e0f.r2.dev/sections/marathon-bg.jpg'
                                        alt='Runner on the trail'
                                        className='aspect-[4/3] w-full object-cover'
                                    />
                                    <div className='absolute inset-0 bg-gradient-to-t from-[#0b0b0d] via-transparent to-transparent' />
                                    <div className='glass absolute bottom-4 left-4 rounded-2xl px-4 py-2.5'>
                                        <div className='text-[9px] font-bold tracking-[0.18em] text-[#E94E1B]'>
                                            COMMUNITY
                                        </div>
                                        <div className='text-base font-bold text-white'>
                                            2.4M runners
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                            <Reveal delay={120}>
                                <ContactForm />
                            </Reveal>
                        </div>
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    )
}

export default Contact
